import { Fragment } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Text } from 'components';
import { FontSize } from 'commons';
import * as S from './styles';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  changeCurrentPage: (page: number) => void;
  isLoading: boolean;
  numberOfPagesToShow?: number;
}

export function Pagination({
  totalPages,
  currentPage,
  changeCurrentPage,
  isLoading,
  numberOfPagesToShow = 5,
}: PaginationProps) {
  const paginationHalf = Math.floor(numberOfPagesToShow / 2);
  const paginationHalfArray = Array.from(Array(paginationHalf).keys());

  const canUseNext = paginationHalf - currentPage >= 0;
  const nextArray = Array.from(
    Array(canUseNext ? paginationHalf - currentPage : 0).keys()
  );

  const isPrevAvailable = currentPage > 0;
  const isNextAvailable = currentPage + 1 < totalPages;

  const prevCalc1 = currentPage - numberOfPagesToShow;
  const prevCalc2 = numberOfPagesToShow - totalPages;
  const canUsePrev = prevCalc1 + prevCalc2 + paginationHalf + 1 > 0;
  const prevArray = Array.from(
    Array(canUsePrev ? prevCalc1 + prevCalc2 + paginationHalf + 1 : 0).keys()
  );

  const hasOverflow = totalPages > numberOfPagesToShow;
  const renderItems = Array.from(Array(totalPages).keys())
    .filter((_, index) => {
      const isPreviousPage = paginationHalfArray.some((cur) => {
        if (!cur) return false;

        return index + cur === currentPage;
      });

      const isNextPage = paginationHalfArray.some((cur) => {
        if (!cur) return false;

        return index === currentPage + cur;
      });

      const isFullNext = nextArray.some((cur) => {
        return index === currentPage + cur + paginationHalf;
      });

      const isFullPrev = prevArray.some((cur) => {
        return index + cur + paginationHalf === currentPage;
      });

      const isFirstPage = index === 0;
      const isSamePage = index === currentPage;
      const isLastPage = index === totalPages - 1;

      return (
        isFirstPage ||
        isFullPrev ||
        isPreviousPage ||
        isSamePage ||
        isNextPage ||
        isFullNext ||
        isLastPage
      );
    })
    .map((num) => {
      const displayPage = num + 1;
      const isFirstElement = displayPage === 1;
      const isLastElement = displayPage === totalPages;
      const canShowFistEllipsis = currentPage > paginationHalf;
      const canShowLastEllipsis = currentPage < totalPages - paginationHalf - 1;

      return (
        <Fragment key={num}>
          {hasOverflow && canShowLastEllipsis && isLastElement && <p>...</p>}

          <S.PageNumberButton
            disabled={isLoading}
            $active={currentPage === num}
            onClick={() => changeCurrentPage(num)}
          >
            <Text size={FontSize.font12}>{displayPage}</Text>
          </S.PageNumberButton>

          {hasOverflow && canShowFistEllipsis && isFirstElement && <p>...</p>}
        </Fragment>
      );
    });

  function nextPage() {
    if (isNextAvailable) changeCurrentPage(currentPage + 1);
  }

  function prevPage() {
    if (isPrevAvailable) changeCurrentPage(currentPage - 1);
  }

  return (
    <S.Wrapper>
      <S.ArrowButton
        disabled={!isPrevAvailable || isLoading}
        onClick={prevPage}
      >
        <MdChevronLeft />
      </S.ArrowButton>

      {renderItems}

      <S.ArrowButton
        disabled={!isNextAvailable || isLoading}
        onClick={nextPage}
      >
        <MdChevronRight />
      </S.ArrowButton>
    </S.Wrapper>
  );
}
